export const CONTACT_FORM_KEYS = [
    "general_contact",
    "demo_request",
    "enterprise_inquiry",
    "phin_inquiry",
    "support",
    "product_request",
    "footwatch_schedule_request",
    "patient_feedback",
    "patient_help",
    "clinician_help",
    "clinician_study_inquiry",
    "email_signup",
    "waitlist",
] as const;

export type ContactFormKey = (typeof CONTACT_FORM_KEYS)[number];

export interface SubmitContactFormParams {
    formKey: ContactFormKey;
    fields: Record<string, string>;
    source?: string;
    /** Honeypot - must stay empty */
    gotcha?: string;
}

export interface SubmitContactFormOptions {
    supabaseUrl: string;
    supabaseAnonKey: string;
}

export class ContactFormSubmitError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ContactFormSubmitError";
    }
}

export async function submitContactForm(
    params: SubmitContactFormParams,
    options: SubmitContactFormOptions
): Promise<void> {
    const { supabaseUrl, supabaseAnonKey } = options;
    if (!supabaseUrl || !supabaseAnonKey) {
        throw new ContactFormSubmitError("Supabase is not configured");
    }

    const response = await fetch(`${supabaseUrl.replace(/\/$/, "")}/functions/v1/public-email`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabaseAnonKey}`,
            apikey: supabaseAnonKey,
        },
        body: JSON.stringify({
            form_key: params.formKey,
            fields: params.fields,
            source: params.source,
            _gotcha: params.gotcha ?? "",
        }),
    });

    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    if (!response.ok) {
        throw new ContactFormSubmitError(data?.error ?? "Failed to submit form");
    }
}

export function useContactFormSubmit(getOptions: () => SubmitContactFormOptions) {
    return async (params: SubmitContactFormParams) => submitContactForm(params, getOptions());
}
